import { Fragment, useEffect, useState } from "react";
import { FaPlus, FaBuilding } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useToken from "./useToken";
import Switch from "react-switch";
import { toast } from "react-toastify";
import { CreateCompanyModal } from "./CompanyDashboard";

const DEFAULT_ANIMATOR = {
  firstname: "",
  name: "",
  email: "",
  location: "",
  company: "",
  is_admin: false,
  can_choose_rounds: false,
  is_anim_serious_game: false,
  is_anim_workshop: true,
  list_configs_id: ["standard-adour", "standard-artois", "standard_loire", "standard-rhin", "standard-rhone", "standard-seine"],
  list_configs_id_serious_game: [],
};

// Plateformes proposées comme lien de connexion dans l'e-mail de création de compte
const LOGIN_PLATFORMS = {
  compte_gouttes: { label: "app.compte-gouttes.fr", toggleLabel: "Animateur plateforme Compte-Gouttes" },
  hydros_workshop: { label: "workshop.hydros-consulting.com", toggleLabel: "Animateur nouvelle plateforme" },
};

// Suggestion : la nouvelle plateforme seulement si c'est la seule activée
function suggestLoginPlatform(animator) {
  return animator.is_anim_serious_game && !animator.is_anim_workshop ? "hydros_workshop" : "compte_gouttes";
}

function loginPlatformWarning(animator, platform) {
  if (!animator.is_anim_serious_game && !animator.is_anim_workshop) {
    return "Aucune plateforme n'est activée pour cet animateur.";
  }
  const hasAccess = platform === "hydros_workshop" ? animator.is_anim_serious_game : animator.is_anim_workshop;
  if (!hasAccess) {
    return `Le lien envoyé pointe vers ${LOGIN_PLATFORMS[platform].label}, mais « ${LOGIN_PLATFORMS[platform].toggleLabel} » n'est pas activé.`;
  }
  return null;
}

function AnimatorsPage() {
  const navigate = useNavigate();
  const [animators, setAnimators] = useState([]);
  const [gameConfigs, setGameConfigs] = useState({ workshop: [], seriousGame: [] });
  const [lastGames, setLastGames] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAnimator, setSelectedAnimator] = useState(null);
  const [viewMode, setViewMode] = useState("dashboard");
  const [newAnimator, setNewAnimator] = useState(DEFAULT_ANIMATOR);
  // null = suivre la suggestion calculée à partir des interrupteurs de plateforme
  const [loginPlatformOverride, setLoginPlatformOverride] = useState(null);
  const [loadingAnimators, setLoadingAnimators] = useState(true);
  const [loadingAddAnimator, setLoadingAddAnimator] = useState(false);
  const { token } = useToken();
  const [dashboardStats, setDashboardStats] = useState({
    number_done_workshop: 0,
    number_waiting_workshop: 0,
    number_people_workshop: 0,
    number_archived_workshop: 0,
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  useEffect(() => {
    const fetchAnimators = async () => {
      setLoadingAnimators(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/get_all_anims`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
        setAnimators(sortedData);
        console.log("Animators fetched successfully:", sortedData);
      } catch (error) {
        console.error("Error fetching animators:", error);
      } finally {
        setLoadingAnimators(false);
      }
    };

    const fetchGameConfigs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/game_config_list_both`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        const sortedWorkshopConfigs = data.workshop_configs.sort((a, b) => a.name.localeCompare(b.name));
        const sortedSeriousGameConfigs = data.serious_game_configs.sort((a, b) => a.name.localeCompare(b.name));
        setGameConfigs({
          workshop: sortedWorkshopConfigs,
          seriousGame: sortedSeriousGameConfigs,
        });
      } catch (error) {
        console.error("Error fetching game configs:", error);
      }
    };

    const fetchLastGames = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/load_last_games`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        const data = await response.json();
        setLastGames(data);
      } catch (error) {
        console.error("Error fetching ten last games:", error);
      }
    };

    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const userInfo = await response.json();
        setIsSuperAdmin(userInfo.is_super_admin || false);
        console.log("User info fetched successfully:", userInfo);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/get_companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCompanies(data || []);
      } catch (e) {
        console.error("Error fetching companies:", e);
      }
    };

    fetchAnimators();
    fetchGameConfigs();
    fetchLastGames();
    fetchUserInfo();
    fetchCompanies();
  }, [token]);

  const fetchDashboardStats = async (animatorId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile_list_games_all/${animatorId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const games = await response.json();

      setDashboardStats({
        number_done_workshop: games.filter(
          (game) => game.status === "finished" && !game.is_archived
        ).length,
        number_waiting_workshop: games.filter(
          (game) => game.status === "not_started" && !game.is_archived
        ).length,
        number_people_workshop: games
          .filter((game) => game.status === "finished" && !game.is_archived)
          .reduce((acc, game) => acc + (game.number_participants ?? game.number_player), 0),
        number_archived_workshop: games.filter((game) => game.is_archived).length,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  };

  const handleAddOrEditAnimator = async (e) => {
    e.preventDefault();
    setLoadingAddAnimator(true);
    try {
      const url = selectedAnimator
        ? `${process.env.REACT_APP_API_URL}/update_account`
        : `${process.env.REACT_APP_API_URL}/create_account`;
      const method = selectedAnimator ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          selectedAnimator
            ? { ...newAnimator, _id: selectedAnimator._id }
            : { ...newAnimator, login_platform: loginPlatform }
        ),
      });

      if (response.ok) {
        toast.success(
          selectedAnimator ? "Animateur modifié avec succès !" : "Animateur ajouté avec succès !"
        );
        setNewAnimator(DEFAULT_ANIMATOR);
        setLoginPlatformOverride(null);
        setSelectedAnimator(null);
        const updatedAnimators = await fetch(`${process.env.REACT_APP_API_URL}/get_all_anims`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await updatedAnimators.json();
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setAnimators(sortedData);
        setShowAddForm(false);
      } else {
        toast.error("Échec de l'opération.");
      }
    } catch (error) {
      toast.error("Erreur lors de l'opération.");
      console.error("Error:", error);
    } finally {
      setLoadingAddAnimator(false);
    }
  };

  const handleToggleAddForm = () => {
    if (showAddForm) {
      setShowAddForm(false); // Hide the add form
    } else {
      setShowAddForm(true);
      setSelectedAnimator(null);
      setViewMode("dashboard");
      setNewAnimator(DEFAULT_ANIMATOR);
      setLoginPlatformOverride(null);
    }
  };

  const suggestedLoginPlatform = suggestLoginPlatform(newAnimator);
  const loginPlatform = loginPlatformOverride || suggestedLoginPlatform;
  const loginWarning = loginPlatformWarning(newAnimator, loginPlatform);

  const handleConfigToggle = (configId, checked) => {
    setNewAnimator((prev) => ({
      ...prev,
      list_configs_id: checked
        ? [...prev.list_configs_id, configId]
        : prev.list_configs_id.filter((id) => id !== configId),
    }));
  };

  const handleSeriousGameToggle = (checked) => {
    setNewAnimator((prev) => ({
      ...prev,
      is_anim_serious_game: checked,
      list_configs_id_serious_game: checked
        ? gameConfigs.seriousGame.map((config) => config._id) // Enable all configs
        : [], // Disable all configs
    }));
  };

  const handleSelectAnimator = (animator) => {
    if (selectedAnimator?._id === animator._id) {
      setSelectedAnimator(null);
      setViewMode("dashboard");
    } else {
      setSelectedAnimator(animator); // Show the dashboard-edit section
      setShowAddForm(false); // Hide the add form
      setNewAnimator({
        firstname: animator.firstname,
        name: animator.name,
        email: animator.email,
        location: animator.location || "",
        company: animator.company || "",
        is_admin: animator.is_admin || false,
        can_choose_rounds: animator.can_choose_rounds || false,
        language: animator.language || "fr",
        is_anim_serious_game: animator.is_anim_serious_game || false,
        is_anim_workshop: animator.is_anim_workshop || true,
        list_configs_id: animator.list_configs_id || [],
        list_configs_id_serious_game: animator.list_configs_id_serious_game || [],
      });
      fetchDashboardStats(animator._id); // Fetch dashboard stats for the selected animator
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return " ⇅";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const filteredAnimators = animators
    .filter((a) => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        a.name?.toLowerCase().includes(q) ||
        a.firstname?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q);
      const matchCompany = !filterCompany || a.company === filterCompany;
      return matchSearch && matchCompany;
    })
    .sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toLowerCase();
      const valB = (b[sortConfig.key] || "").toLowerCase();
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

  return (
    <Fragment>
      {loadingAnimators ? (
        <div className="loader-container">
          <img src="/loader.gif" className="loader" alt="Loading..." />
        </div>
      ) : (
        <div className="animators-page">
          <div className="left-section">
            <div className="title-container">
              <h1 className="blue-title">Liste des animateurs</h1>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="orange-button add-anim-button"
                  style={{ backgroundColor: "var(--hydros-blue)", display: "flex", alignItems: "center", gap: "6px" }}
                  onClick={() => navigate("/company_dashboard")}
                >
                  <FaBuilding /> Dashboard entreprise
                </button>
                <button className="orange-button add-anim-button" style={{ display: "flex", alignItems: "center", gap: "6px" }} onClick={handleToggleAddForm}>
                  <FaPlus /> Ajouter un animateur
                </button>
              </div>
            </div>
            <div className="animators-search-bar">
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou email…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="animators-search-input"
              />
              <select
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                className="animators-filter-select"
              >
                <option value="">Toutes les entreprises</option>
                {companies.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              <span className="animators-count">{filteredAnimators.length} animateur{filteredAnimators.length > 1 ? "s" : ""}</span>
            </div>
            <div className="table-container">
              <table className="animators-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>Nom{sortIcon("name")}</th>
                    <th onClick={() => handleSort("firstname")} style={{ cursor: "pointer" }}>Prénom{sortIcon("firstname")}</th>
                    <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>Adresse mail{sortIcon("email")}</th>
                    <th>Entreprise</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnimators.map((animator) => (
                    <tr
                      key={animator._id}
                      className={selectedAnimator?._id === animator._id ? "selected-row" : ""}
                      onClick={() => handleSelectAnimator(animator)}
                    >
                      <td>{animator.name}</td>
                      <td>{animator.firstname}</td>
                      <td>{animator.email}</td>
                      <td>{companies.find((c) => c._id === animator.company)?.name || "—"}</td>
                    </tr>
                  ))}
                  {filteredAnimators.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: "20px" }}>Aucun résultat</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="right-section">
            {selectedAnimator && (
              <div className="dashboard-edit-container">
                <h2
                  className={`dashboard-edit-title ${viewMode === "dashboard" ? "selected-view" : ""}`}
                  onClick={() => setViewMode("dashboard")}
                >
                  Dashboard
                </h2>
                <h2
                  className={`dashboard-edit-title ${viewMode === "edit" ? "selected-view" : ""}`}
                  onClick={() => setViewMode("edit")}
                >
                  Modifier l'animateur
                </h2>
              </div>
            )}
            {selectedAnimator && viewMode === "dashboard" &&  (
              <div className="dashboard-container">
                <h2 className="animator-name">{selectedAnimator.firstname} {selectedAnimator.name}</h2>
                <div className="dashboard-stats-wrapper">
                  <div className="dashboard-stat-container">
                    <div className="dashboard-stat-value">{dashboardStats.number_done_workshop}</div>
                    <div className="dashboard-stat-header">Ateliers animés</div>
                    <div>
                      <img className="dashboard-stat-logo" src="/atelier.png" alt="Ateliers animés" />
                    </div>
                  </div>
                  <div className="dashboard-stat-container">
                    <div className="dashboard-stat-value">{dashboardStats.number_people_workshop}</div>
                    <div className="dashboard-stat-header">Personnes formées</div>
                    <div>
                      <img className="dashboard-stat-logo" src="/reseau.png" alt="Personnes formées" />
                    </div>
                  </div>
                  <div className="dashboard-stat-container">
                    <div className="dashboard-stat-value">{dashboardStats.number_waiting_workshop}</div>
                    <div className="dashboard-stat-header">Ateliers en attente</div>
                    <div>
                      <img className="dashboard-stat-logo" src="/time-and-calendar.png" alt="Ateliers en attente" />
                    </div>
                  </div>
                  <div className="dashboard-stat-container">
                    <div className="dashboard-stat-value">{dashboardStats.number_archived_workshop}</div>
                    <div className="dashboard-stat-header">Ateliers archivés</div>
                    <div>
                      <img className="dashboard-stat-logo" src="/folder.png" alt="Ateliers archivés" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showAddForm && (
              <form className="add-animator-form" onSubmit={handleAddOrEditAnimator}>
                <h1 className="blue-title">Ajouter un animateur</h1>
                <div className="name-container">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input
                      type="text"
                      value={newAnimator.firstname}
                      onChange={(e) => setNewAnimator({ ...newAnimator, firstname: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      value={newAnimator.name}
                      onChange={(e) => setNewAnimator({ ...newAnimator, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newAnimator.email}
                    onChange={(e) => setNewAnimator({ ...newAnimator, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Localisation</label>
                  <input
                    type="text"
                    value={newAnimator.location}
                    onChange={(e) => setNewAnimator({ ...newAnimator, location: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Langue</label>
                  <select
                    value={newAnimator.language}
                    onChange={(e) =>
                      setNewAnimator({ ...newAnimator, language: e.target.value })
                    }
                    required
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <select
                    value={newAnimator.company || ""}
                    onChange={(e) => {
                      if (e.target.value === "__new__") {
                        setShowCreateCompanyModal(true);
                      } else {
                        setNewAnimator({ ...newAnimator, company: e.target.value });
                      }
                    }}
                  >
                    <option value="">— Aucune entreprise —</option>
                    {companies.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                    <option value="__new__">+ Créer une nouvelle entreprise</option>
                  </select>
                </div>
                {isSuperAdmin && (
                  <div className="form-group toggle-group">
                    <label>Administrateur de la plateforme</label>
                    <Switch
                      checked={newAnimator.is_admin}
                      onChange={(checked) => setNewAnimator({ ...newAnimator, is_admin: checked })}
                      onColor="#db6c52"
                      offColor="#205673"
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </div>
                )}
                {isSuperAdmin && (
                  <div className="form-group toggle-group">
                    <label>Choix du nombre de tours</label>
                    <Switch
                      checked={newAnimator.can_choose_rounds}
                      onChange={(checked) => setNewAnimator({ ...newAnimator, can_choose_rounds: checked })}
                      onColor="#db6c52"
                      offColor="#205673"
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </div>
                )}
                <div className="form-group toggle-group">
                  <label>Animateur nouvelle plateforme</label>
                  <Switch
                    checked={newAnimator.is_anim_serious_game}
                    onChange={handleSeriousGameToggle}
                    onColor="#db6c52"
                    offColor="#205673"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                <div className="form-group toggle-group">
                  <label>Animateur plateforme Compte-Gouttes</label>
                  <Switch
                    checked={newAnimator.is_anim_workshop}
                    onChange={(checked) =>
                      setNewAnimator({ ...newAnimator, is_anim_workshop: checked })
                    }
                    onColor="#db6c52"
                    offColor="#205673"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                <div className="form-group">
                  <label>Configurations disponibles ancienne plateforme</label>
                  <div className="scrollable-configs-container">
                    {gameConfigs.workshop.map((config) => (
                      <div key={config._id} className="toggle-group">
                        <span>{config.name}</span>
                        <Switch
                          checked={newAnimator.list_configs_id.includes(config._id)}
                          onChange={(checked) => handleConfigToggle(config._id, checked)}
                          onColor="#db6c52"
                          offColor="#205673"
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Configurations disponibles nouvelle plateforme</label>
                  <div className="scrollable-configs-container">
                    {gameConfigs.seriousGame.map((config) => (
                      <div key={config._id} className="toggle-group">
                        <span>{config.name}</span>
                        <Switch
                          checked={newAnimator.list_configs_id_serious_game.includes(config._id)}
                          onChange={(checked) =>
                            setNewAnimator((prev) => ({
                              ...prev,
                              list_configs_id_serious_game: checked
                                ? [...prev.list_configs_id_serious_game, config._id]
                                : prev.list_configs_id_serious_game.filter((id) => id !== config._id),
                            }))
                          }
                          onColor="#db6c52"
                          offColor="#205673"
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Lien de connexion envoyé par e-mail</label>
                  <select
                    value={loginPlatform}
                    onChange={(e) => setLoginPlatformOverride(e.target.value)}
                  >
                    {Object.entries(LOGIN_PLATFORMS).map(([key, platform]) => (
                      <option key={key} value={key}>
                        {platform.label}{key === suggestedLoginPlatform ? " (suggéré)" : ""}
                      </option>
                    ))}
                  </select>
                  {loginWarning && <div className="login-platform-warning">⚠ {loginWarning}</div>}
                </div>
                <button
                  type="submit"
                  className={`orange-button add-anim-button ${loadingAddAnimator ? "loading-button" : ""}`}
                  disabled={loadingAddAnimator}
                >
                  {loadingAddAnimator ? "Ajout en cours..." : "Ajouter"}
                </button>
              </form>
            )}
            {selectedAnimator && viewMode === "edit" && (
              <form className="add-animator-form" onSubmit={handleAddOrEditAnimator}>
                <div className="name-container">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input
                      type="text"
                      value={newAnimator.firstname}
                      onChange={(e) => setNewAnimator({ ...newAnimator, firstname: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      value={newAnimator.name}
                      onChange={(e) => setNewAnimator({ ...newAnimator, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newAnimator.email}
                    onChange={(e) => setNewAnimator({ ...newAnimator, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Localisation</label>
                  <input
                    type="text"
                    value={newAnimator.location}
                    onChange={(e) => setNewAnimator({ ...newAnimator, location: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Langue</label>
                  <select
                    value={newAnimator.language}
                    onChange={(e) =>
                      setNewAnimator({ ...newAnimator, language: e.target.value })
                    }
                    required
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <select
                    value={newAnimator.company || ""}
                    onChange={(e) => {
                      if (e.target.value === "__new__") {
                        setShowCreateCompanyModal(true);
                      } else {
                        setNewAnimator({ ...newAnimator, company: e.target.value });
                      }
                    }}
                  >
                    <option value="">— Aucune entreprise —</option>
                    {companies.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                    <option value="__new__">+ Créer une nouvelle entreprise</option>
                  </select>
                </div>
                {isSuperAdmin && (
                  <div className="form-group toggle-group">
                    <label>Administrateur de la plateforme</label>
                    <Switch
                      checked={newAnimator.is_admin}
                      onChange={(checked) => setNewAnimator({ ...newAnimator, is_admin: checked })}
                      onColor="#db6c52"
                      offColor="#205673"
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </div>
                )}
                {isSuperAdmin && (
                  <div className="form-group toggle-group">
                    <label>Choix du nombre de tours</label>
                    <Switch
                      checked={newAnimator.can_choose_rounds}
                      onChange={(checked) => setNewAnimator({ ...newAnimator, can_choose_rounds: checked })}
                      onColor="#db6c52"
                      offColor="#205673"
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </div>
                )}
                <div className="form-group toggle-group">
                  <label>Animateur nouvelle plateforme</label>
                  <Switch
                    checked={newAnimator.is_anim_serious_game}
                    onChange={handleSeriousGameToggle}
                    onColor="#db6c52"
                    offColor="#205673"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                <div className="form-group toggle-group">
                  <label>Animateur plateforme Compte-Gouttes</label>
                  <Switch
                    checked={newAnimator.is_anim_workshop}
                    onChange={(checked) =>
                      setNewAnimator({ ...newAnimator, is_anim_workshop: checked })
                    }
                    onColor="#db6c52"
                    offColor="#205673"
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                <div className="form-group">
                  <label>Configurations disponibles ancienne plateforme</label>
                  <div className="scrollable-configs-container">
                    {gameConfigs.workshop.map((config) => (
                      <div key={config._id} className="toggle-group">
                        <span>{config.name}</span>
                        <Switch
                          checked={newAnimator.list_configs_id.includes(config._id)}
                          onChange={(checked) => handleConfigToggle(config._id, checked)}
                          onColor="#db6c52"
                          offColor="#205673"
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Configurations disponibles nouvelle plateforme</label>
                  <div className="scrollable-configs-container">
                    {gameConfigs.seriousGame.map((config) => (
                      <div key={config._id} className="toggle-group">
                        <span>{config.name}</span>
                        <Switch
                          checked={newAnimator.list_configs_id_serious_game.includes(config._id)}
                          onChange={(checked) =>
                            setNewAnimator((prev) => ({
                              ...prev,
                              list_configs_id_serious_game: checked
                                ? [...prev.list_configs_id_serious_game, config._id]
                                : prev.list_configs_id_serious_game.filter((id) => id !== config._id),
                            }))
                          }
                          onColor="#db6c52"
                          offColor="#205673"
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  type="submit"
                  className={`orange-button add-anim-button ${loadingAddAnimator ? "loading-button" : ""}`}
                  disabled={loadingAddAnimator}
                >
                  {loadingAddAnimator ? "Modification en cours..." : "Modifier"}
                </button>
              </form>
            )}
            
            {!selectedAnimator && !showAddForm && (
                <div className="table-last-games">
                    <h1 className="blue-title">Derniers ateliers animés</h1>
                    
                    <ul>
                    {lastGames.map((game) => {
                        const date = new Date(game.created_date);
                        const formattedDate = date.toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        });

                    return (
                    <li key={game._id} className="game-grid-item">
                        <div className="game-grid">
                          <div><strong>Date :</strong> {formattedDate}</div>
                          <div>
                            <strong>Animateur :</strong> {
                            (() => {
                                const animator = animators.find(a => a._id === game.animator_id);
                                return animator ? `${animator.firstname} ${animator.name}` : "Non défini";
                            })()
                            }
                          </div>
                          <div><strong>Nom :</strong> {game.workshop_name && game.workshop_name.trim() !== "" ? game.workshop_name : "Non défini"}</div>
                          <div>
                            <strong>Statut :</strong> {
                            game.status === "not_started" ? "Non démarré" :
                            game.status === "started" ? "Démarré" :
                            game.status === "finished" ? "Terminé" :
                            "Inconnu"
                            }
                        </div>
                        </div>
                      </li>
                    );
                    })}
                    </ul>
                </div>
            )}
          </div>
        </div>
      )}
      {showCreateCompanyModal && (
        <CreateCompanyModal
          token={token}
          onClose={() => setShowCreateCompanyModal(false)}
          onSaved={(newId) => {
            fetch(`${process.env.REACT_APP_API_URL}/get_companies`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => r.json())
              .then((data) => {
                setCompanies(data || []);
                if (newId) setNewAnimator((prev) => ({ ...prev, company: newId }));
              });
          }}
        />
      )}
    </Fragment>
  );
}

export default AnimatorsPage;
